import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Grid,
    Alert,
    CircularProgress,
    Paper,
    Divider
} from '@mui/material';
import {
    PlayArrow as OptimizeIcon,
    Refresh as ResetIcon
} from '@mui/icons-material';
import { useAuth } from '@vortx/shared-ui';
import ZoneEditor from '../components/ZoneEditor.jsx';
import ResultsChart from '../components/ResultsChart.jsx';
import { optimizeFacility } from '../services/coldStorageService';

// Default values for quick testing
const DEFAULT_AMBIENT_TEMPS = Array(24).fill(0).map((_, i) => {
    // Sinusoidal temperature pattern (40°F to 80°F over 24 hours)
    return 60 + 20 * Math.sin((i - 6) * Math.PI / 12);
});

const DEFAULT_ENERGY_PRICES = Array(24).fill(0).map((_, i) => {
    // Peak pricing pattern (higher during business hours)
    if (i >= 12 && i < 18) return 0.25;  // Peak hours
    if (i >= 8 && i < 22) return 0.15;   // Mid-peak
    return 0.08;  // Off-peak
});

const DEFAULT_DEMAND_CHARGE = 15.0;  // $/kW

/**
 * ColdStorageOptimization - Main page for cold storage facility optimization
 *
 * Features:
 * - Configure facility zones (freezers, coolers, prep rooms)
 * - Set ambient temperature forecast
 * - Configure energy prices and demand charges
 * - Run optimization
 * - View results (load curve, costs, temperatures)
 */
export default function ColdStorageOptimization() {
    const { currentUser } = useAuth();
    const [zones, setZones] = useState([]);
    const [ambientTemps, setAmbientTemps] = useState(DEFAULT_AMBIENT_TEMPS.join(', '));
    const [energyPrices, setEnergyPrices] = useState(DEFAULT_ENERGY_PRICES.join(', '));
    const [demandCharge, setDemandCharge] = useState(DEFAULT_DEMAND_CHARGE);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [results, setResults] = useState(null);

    const handleOptimize = async () => {
        setError(null);
        setLoading(true);

        try {
            // Validate inputs
            if (zones.length === 0) {
                throw new Error('Please add at least one zone');
            }

            // Parse ambient temps
            const ambientArray = ambientTemps.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n));
            if (ambientArray.length !== 24) {
                throw new Error('Ambient temps must have exactly 24 values (one per hour)');
            }

            // Parse energy prices
            const pricesArray = energyPrices.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n));
            if (pricesArray.length !== 24) {
                throw new Error('Energy prices must have exactly 24 values (one per hour)');
            }

            // Build facility config
            const facilityConfig = {
                zones: zones.map(zone => ({
                    name: zone.name,
                    temp_min: zone.temp_min,
                    temp_max: zone.temp_max,
                    thermal_resistance: zone.thermal_resistance,
                    thermal_capacitance: zone.thermal_capacitance,
                    compressor_power_kw: zone.compressor_power_kw,
                    compressor_cop: zone.compressor_cop
                }))
            };

            // Call API
            const result = await optimizeFacility(
                facilityConfig,
                ambientArray,
                pricesArray,
                parseFloat(demandCharge),
                currentUser
            );

            setResults(result);
        } catch (err) {
            console.error('Optimization failed:', err);
            setError(err.message || 'Optimization failed');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setZones([]);
        setAmbientTemps(DEFAULT_AMBIENT_TEMPS.join(', '));
        setEnergyPrices(DEFAULT_ENERGY_PRICES.join(', '));
        setDemandCharge(DEFAULT_DEMAND_CHARGE);
        setResults(null);
        setError(null);
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Page Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Flux COLD Optimization
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    Configure your cold storage facility and optimize compressor schedules to minimize energy costs.
                </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Configuration Panel */}
                <Grid item xs={12} lg={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Facility Configuration
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        {/* Zone Editor */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Zones
                            </Typography>
                            <ZoneEditor zones={zones} onChange={setZones} />
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Ambient Temperature */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Ambient Temperature Forecast (°F)
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                value={ambientTemps}
                                onChange={(e) => setAmbientTemps(e.target.value)}
                                helperText="24 comma-separated values (one per hour)"
                                placeholder="e.g., 60, 62, 65, 70, ..."
                            />
                        </Box>

                        {/* Energy Prices */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Energy Prices ($/kWh)
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                value={energyPrices}
                                onChange={(e) => setEnergyPrices(e.target.value)}
                                helperText="24 comma-separated values (one per hour)"
                                placeholder="e.g., 0.08, 0.08, 0.10, 0.15, ..."
                            />
                        </Box>

                        {/* Demand Charge */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Demand Charge ($/kW)
                            </Typography>
                            <TextField
                                fullWidth
                                type="number"
                                value={demandCharge}
                                onChange={(e) => setDemandCharge(e.target.value)}
                                helperText="Monthly demand charge rate"
                            />
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                            <Button
                                variant="contained"
                                startIcon={loading ? <CircularProgress size={20} /> : <OptimizeIcon />}
                                onClick={handleOptimize}
                                disabled={loading || zones.length === 0}
                                fullWidth
                            >
                                {loading ? 'Optimizing...' : 'Optimize'}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<ResetIcon />}
                                onClick={handleReset}
                                disabled={loading}
                            >
                                Reset
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                {/* Results Panel */}
                <Grid item xs={12} lg={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Optimization Results
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        <ResultsChart results={results} zones={zones} />
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
